// F4c-4: product edit route (stitch-react — replaces Phase 4a placeholder).
//
// Responsibilities:
//   - Read `:productId` from the URL via `useParams`.
//   - Call `useProduct(productId)` — `data === null` when the product
//     is genuinely not found (not an error).
//   - Render one of four branches:
//       1. isLoading  → skeleton matching the form layout
//       2. isError    → inline error card with a translated retry CTA
//       3. data === null → friendly not-found card (reuses detail copy)
//       4. data present → <ProductForm mode="edit" …/> wired to
//                         `useUpdateProduct`
//   - On success: navigate to `/products/:productId` (back to detail)
//   - On error: map via `mapProductError` and surface via the form's
//     `submitError` prop.
//   - On cancel: navigate back to `/products/:productId`.
//
// The F4c-2 `ProductForm` does NOT include a Delete button in its
// sticky action bar (only Cancel + Submit). Delete lives on the
// detail route per F-003 design §7, so there is no DeleteProductDialog
// wiring here.
//
// This route is already wrapped in `<AuthGuard mode="require-store">`
// by the router, so no authentication logic is needed here.

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  centsToDisplay,
  mapCategoryError,
  mapProductError,
  useAuthSession,
  useStoreCategories,
  useTranslation,
} from '@dukanti/shared';
import type { ProductErrorKey } from '@dukanti/shared';
import { supabase } from '@/lib/supabase';
import { useProduct } from '@/hooks/use-product';
import { useCurrentStore } from '@/hooks/use-current-store';
import { useUpdateProduct, type UpdateProductImageOrderEntry } from '@/hooks/use-update-product';
import { ProductForm } from '@/features/products/components/product-form';
import { SkeletonBar } from '@/features/products/components/skeleton-bar';
import { ProductNotFoundCard } from '@/features/products/components/product-not-found-card';
import { ProductInlineError } from '@/features/products/components/product-inline-error';
import type {
  ProductFormInitialValues,
  ProductFormSubmitPayload,
} from '@/features/products/components/product-form';

export function ProductEditRoute() {
  const { t, dir } = useTranslation();
  const navigate = useNavigate();
  const { productId } = useParams<{ productId: string }>();
  const { data, isLoading, isError, refetch } = useProduct(productId);

  const authSession = useAuthSession(supabase);
  const { storeId } = useCurrentStore(authSession);
  const categories = useStoreCategories(supabase, storeId);
  const updateProduct = useUpdateProduct();
  const [submitError, setSubmitError] = useState<ProductErrorKey | null>(null);

  const handleCancel = () => {
    if (productId) {
      navigate(`/products/${productId}`);
    } else {
      navigate('/products');
    }
  };

  const handleSubmit = async (payload: ProductFormSubmitPayload) => {
    setSubmitError(null);

    // AuthGuard already enforces store membership, but guard defensively
    // so TypeScript is happy and we never hit the mutation with a null.
    if (!productId || !storeId || !data) {
      setSubmitError('common.errors.permissionDenied');
      return;
    }

    // Map ProductForm slots → the mutation's `finalOrder` shape.
    // Each slot is either an existing image (carry its id) or a newly
    // picked file (carry its index within `payload.newFiles`). We track
    // `newFileIndex` locally because `payload.newFiles` is built in the
    // same slot iteration order inside ProductForm, so the two indices
    // line up 1:1.
    const finalOrder: UpdateProductImageOrderEntry[] = [];
    let newFileIndex = 0;
    for (const slot of payload.slots) {
      if (slot.kind === 'existing' && slot.existingImage) {
        finalOrder.push({ kind: 'existing', id: slot.existingImage.id });
      } else if (slot.kind === 'new' && slot.file) {
        finalOrder.push({ kind: 'new', index: newFileIndex });
        newFileIndex += 1;
      }
    }

    try {
      await updateProduct.mutateAsync({
        productId,
        storeId,
        input: payload.input,
        mode: payload.mode,
        options: payload.options,
        variants: payload.variants,
        newFiles: payload.newFiles,
        removedImageIds: payload.removedExistingImageIds,
        existingImages: data.images,
        finalOrder,
      });

      // Global `<Toaster />` is mounted in `apps/admin/src/app.tsx`
      // (F4d-4); sonner handles queueing, auto-dismiss, and RTL.
      toast.success(t('admin.products.toasts.updated'));

      navigate(`/products/${productId}`, { replace: true });
    } catch (err) {
      console.error('[product-edit-route]', err);
      setSubmitError(mapProductError(err));
    }
  };

  return (
    <main dir={dir} data-testid="product-edit-route" className="bg-background min-h-screen">
      {isLoading ? (
        <ProductEditSkeleton loadingLabel={t('common.loading')} />
      ) : isError ? (
        <ProductInlineError
          title={t('common.error')}
          retryLabel={t('common.retry')}
          onRetry={() => {
            void refetch();
          }}
          backLabel={t('admin.products.detail.notFoundCta')}
          testIdPrefix="product-edit"
        />
      ) : data === null ? (
        <ProductNotFoundCard
          title={t('admin.products.detail.notFoundTitle')}
          description={t('admin.products.detail.notFoundDescription')}
          ctaLabel={t('admin.products.detail.notFoundCta')}
          testIdPrefix="product-edit"
        />
      ) : data ? (
        <ProductForm
          mode="edit"
          initialValues={buildInitialValues(data)}
          categories={mergeCurrentCategory(categories.categories, data)}
          isLoadingCategories={categories.isLoading}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          submitError={
            submitError ?? (categories.isError ? mapCategoryError(categories.error) : null)
          }
          isSubmitting={updateProduct.isPending}
        />
      ) : null}
    </main>
  );
}

// ---------- Initial values builder ----------

function buildInitialValues(
  data: NonNullable<ReturnType<typeof useProduct>['data']>,
): ProductFormInitialValues {
  // `centsToDisplay` returns a JS number (e.g., 19.99). The form's
  // Zod resolver parses `priceRaw` as a locale-aware string, so we
  // format with `.toFixed(2)` to guarantee exactly two decimals and
  // an ASCII dot separator regardless of locale — `parseLocalePrice`
  // accepts that shape in every supported locale.
  const display = centsToDisplay(data.price_cents);
  const priceRaw = Number.isFinite(display) ? display.toFixed(2) : '';
  const saleDisplay =
    data.sale_price_cents !== null && data.sale_price_cents !== undefined
      ? centsToDisplay(data.sale_price_cents)
      : Number.NaN;

  return {
    name: data.name,
    description: data.description ?? '',
    priceRaw,
    saleEnabled: data.sale_price_cents !== null && data.sale_price_cents !== undefined,
    salePriceRaw: Number.isFinite(saleDisplay) ? saleDisplay.toFixed(2) : '',
    inventory: data.inventory,
    categoryId: data.category_id,
    images: data.images,
    mode: data.mode ?? 'simple',
    options: (data.options ?? []).map((option) => ({
      id: option.id,
      name: option.name,
      displayOrder: option.display_order,
      values: (data.optionValues ?? [])
        .filter((value) => value.option_id === option.id)
        .map((value) => ({
          id: value.id,
          value: value.value,
          displayOrder: value.display_order,
        })),
    })),
    variants: (data.variants ?? []).map((variant) => {
      const variantSaleDisplay =
        variant.sale_price_cents !== null && variant.sale_price_cents !== undefined
          ? centsToDisplay(variant.sale_price_cents)
          : Number.NaN;
      return {
        id: variant.id,
        optionValueIds: variant.values.map((value) => value.value_id),
        optionSummary: variant.option_summary,
        priceRaw: centsToDisplay(variant.price_cents).toFixed(2),
        saleEnabled: variant.sale_price_cents !== null && variant.sale_price_cents !== undefined,
        salePriceRaw: Number.isFinite(variantSaleDisplay) ? variantSaleDisplay.toFixed(2) : '',
        inventory: variant.inventory,
        active: variant.active,
        isDefault: variant.is_default,
        imageId: variant.image_id,
        barcodeValue: variant.barcode_value ?? '',
        previousBarcodeNormalized: variant.barcode_normalized,
        barcodeReplaceConfirmed: false,
        barcodeClearConfirmed: false,
        barcodeDeletedReuseConfirmedNormalized: null,
      };
    }),
  };
}

function mergeCurrentCategory(
  categories: NonNullable<ReturnType<typeof useStoreCategories>['categories']>,
  data: NonNullable<ReturnType<typeof useProduct>['data']>,
) {
  if (categories.some((category) => category.id === data.category.id)) {
    return categories;
  }
  return [
    ...categories,
    {
      id: data.category.id,
      store_id: data.store_id,
      name: data.category.name,
      display_order: categories.length,
      created_at: data.created_at,
      updated_at: data.updated_at,
      deleted_at: null,
      media: data.category.image_url
        ? {
            id: data.category.id,
            category_id: data.category.id,
            store_id_cache: data.store_id,
            storage_path: '',
            file_size_bytes: 0,
            created_at: data.created_at,
            url: data.category.image_url,
          }
        : null,
    },
  ];
}

// ---------- Loading skeleton ----------

interface ProductEditSkeletonProps {
  readonly loadingLabel: string;
}

function ProductEditSkeleton({ loadingLabel }: ProductEditSkeletonProps) {
  return (
    <div
      className="mx-auto max-w-screen-md px-6 pb-32 pt-8"
      role="status"
      aria-busy="true"
      aria-live="polite"
      data-testid="product-edit-skeleton"
    >
      <span className="sr-only">{loadingLabel}</span>
      <SkeletonBar className="mb-6 h-4 w-24" />
      <SkeletonBar className="mb-8 h-9 w-64" />
      <div className="border-border bg-card shadow-admin-card rounded-lg border p-6 sm:p-8">
        <SkeletonBar className="mb-6 h-32 w-full rounded-lg" />
        <SkeletonBar className="mb-2 h-4 w-20" />
        <SkeletonBar className="mb-5 h-10 w-full rounded-md" />
        <SkeletonBar className="mb-2 h-4 w-24" />
        <SkeletonBar className="mb-5 h-24 w-full rounded-md" />
        <SkeletonBar className="mb-2 h-4 w-16" />
        <SkeletonBar className="mb-5 h-10 w-full rounded-md" />
        <SkeletonBar className="mb-2 h-4 w-20" />
        <SkeletonBar className="h-10 w-full rounded-md" />
      </div>
    </div>
  );
}
